import { DateRange } from 'react-day-picker';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

export interface DefaultTimeOverrides {
  defaultFromTime?: { hours: number; minutes: number };
  defaultToTime?: 'now' | { hours: number; minutes: number };
}

interface ApplyDateTimeDefaultsOptions extends DefaultTimeOverrides {
  currentDate: DateRange | undefined;
  timeZone: string;
  dateLimit: 'past' | 'future' | 'all';
}

/** Replaces the full date AND time to now. Use for clamping future datetimes. */
const clampDateTimeToNow = (localDate: Date, timeZone: string) => {
  const now = new Date();
  const zonedNow = toZonedTime(now, timeZone);
  localDate.setTime(zonedNow.getTime());
};

/** Sets only the time component to now, preserving the selected date. */
const applyCurrentTime = (localDate: Date, timeZone: string) => {
  const zonedNow = toZonedTime(new Date(), timeZone);
  localDate.setHours(
    zonedNow.getHours(),
    zonedNow.getMinutes(),
    zonedNow.getSeconds(),
    0,
  );
};

const isFutureDatetime = (localDate: Date, timeZone: string) => {
  const dateInUtc = fromZonedTime(localDate, timeZone);
  return dateInUtc > new Date();
};

const isPastDatetime = (localDate: Date, timeZone: string) => {
  const dateInUtc = fromZonedTime(localDate, timeZone);
  return dateInUtc < new Date();
};

const isToday = (localDate: Date, timeZone: string) => {
  const nowInTimeZone = toZonedTime(new Date(), timeZone);
  return (
    localDate.getFullYear() === nowInTimeZone.getFullYear() &&
    localDate.getMonth() === nowInTimeZone.getMonth() &&
    localDate.getDate() === nowInTimeZone.getDate()
  );
};

/**
 * Apply time defaults to a selected date range from the calendar.
 *
 * The calendar sets times to 00:00 — this function restores/applies
 * appropriate times based on current state and configuration.
 *
 * Mutates the from/to dates on selectedDate in place.
 */
export function applyDateTimeDefaults(
  selectedDate: DateRange | undefined,
  options: ApplyDateTimeDefaultsOptions,
): void {
  if (!selectedDate) return;

  const { currentDate, timeZone, dateLimit, defaultFromTime, defaultToTime } =
    options;
  const selectedFromDate = selectedDate.from;
  const selectedToDate = selectedDate.to;

  // --- FROM time ---
  if (selectedFromDate && currentDate?.from) {
    if (defaultFromTime) {
      selectedFromDate.setHours(
        defaultFromTime.hours,
        defaultFromTime.minutes,
        0,
        0,
      );
    } else {
      const zonedDate = toZonedTime(currentDate.from, timeZone);
      selectedFromDate.setHours(
        zonedDate.getHours(),
        zonedDate.getMinutes(),
        0,
        0,
      );
    }
  }

  // --- TO time ---
  if (selectedToDate && currentDate?.to) {
    if (defaultToTime === 'now') {
      applyCurrentTime(selectedToDate, timeZone);
    } else if (defaultToTime) {
      selectedToDate.setHours(defaultToTime.hours, defaultToTime.minutes, 0, 0);
    } else {
      const zonedDate = toZonedTime(currentDate.to, timeZone);
      selectedToDate.setHours(
        zonedDate.getHours(),
        zonedDate.getMinutes(),
        0,
        0,
      );
    }
  }

  // --- dateLimit-specific defaults ---
  if (dateLimit === 'past') {
    // Clamp: past times must not go into the future
    if (selectedToDate && isFutureDatetime(selectedToDate, timeZone)) {
      clampDateTimeToNow(selectedToDate, timeZone);
    }

    // Fresh TO selection defaults
    if (!currentDate?.to && selectedToDate) {
      if (isToday(selectedToDate, timeZone)) {
        applyCurrentTime(selectedToDate, timeZone);
      } else if (isPastDatetime(selectedToDate, timeZone)) {
        if (defaultToTime === 'now') {
          applyCurrentTime(selectedToDate, timeZone);
        } else if (defaultToTime) {
          selectedToDate.setHours(
            defaultToTime.hours,
            defaultToTime.minutes,
            0,
            0,
          );
        } else {
          // Default: 13:00 (past midday)
          selectedToDate.setHours(13, 0, 0, 0);
        }
      }
    }

    // Fresh FROM selection defaults
    if (
      !currentDate?.from &&
      selectedFromDate &&
      isPastDatetime(selectedFromDate, timeZone)
    ) {
      if (defaultFromTime) {
        selectedFromDate.setHours(
          defaultFromTime.hours,
          defaultFromTime.minutes,
          0,
          0,
        );
      } else {
        // Default: 12:00 (midday)
        selectedFromDate.setHours(12, 0, 0, 0);
      }
    }
  } else if (dateLimit === 'future') {
    if (selectedFromDate && isPastDatetime(selectedFromDate, timeZone)) {
      clampDateTimeToNow(selectedFromDate, timeZone);
    }

    if (
      !currentDate?.from &&
      selectedFromDate &&
      isToday(selectedFromDate, timeZone)
    ) {
      applyCurrentTime(selectedFromDate, timeZone);
    }

    if (
      !currentDate?.to &&
      selectedToDate &&
      isFutureDatetime(selectedToDate, timeZone) &&
      !isToday(selectedToDate, timeZone)
    ) {
      // Default: 13:00 (past midday)
      selectedToDate.setHours(13, 0, 0, 0);
    }

    if (
      !currentDate?.from &&
      selectedFromDate &&
      isFutureDatetime(selectedFromDate, timeZone)
    ) {
      // Default: 12:00 (midday)
      selectedFromDate.setHours(12, 0, 0, 0);
    }
  }

  // Ensure to is always after from
  if (selectedFromDate && selectedToDate && selectedFromDate > selectedToDate) {
    selectedFromDate.setHours(
      selectedToDate.getHours(),
      selectedToDate.getMinutes(),
      0,
      0,
    );
  }
}
